// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Data restore API - upload, list, export, and restore operations.
 */
import { apiClient } from './api-client'
import type {
  BaseImage,
  DownloadUrlResponse,
  ExportOptions,
  ExportResponse,
  FinalizeUploadResponse,
  PresignedUploadResponse,
  RestoreConfig,
  RestoreSnapshot,
  RunRestoreResponse,
} from './types/data-restore'

/**
 * Get data restore/download configuration.
 */
export async function getRestoreConfig(): Promise<RestoreConfig> {
  const response = await apiClient.get<RestoreConfig>('/admin/restore/config')
  return response.data
}

/**
 * List available snapshots.
 */
export async function listSnapshots(): Promise<RestoreSnapshot[]> {
  const response = await apiClient.get<RestoreSnapshot[]>('/admin/restore/snapshots')
  return response.data
}

/**
 * Upload progress callback type (percent 0-100).
 */
export type UploadProgressCallback = (percent: number) => void

/**
 * Upload a restore snapshot using presigned URL.
 *
 * Gets presigned URL from backend, uploads zip directly to S3, then finalizes.
 */
export async function uploadSnapshot(
  file: File,
  onProgress?: UploadProgressCallback
): Promise<RestoreSnapshot> {
  // Extract snapshot name from filename (strip .zip extension)
  const snapshotName = file.name.replace(/\.zip$/i, '')

  // Get presigned URL from backend
  const initFormData = new FormData()
  initFormData.append('name', snapshotName)
  const presignedResponse = await apiClient.post<PresignedUploadResponse>(
    '/admin/restore/snapshots/presigned-upload',
    initFormData
  )
  const { uploadUrl, snapshotId } = presignedResponse.data

  // Upload directly to S3 with progress
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress?.(Math.round((e.loaded / e.total) * 100))
      }
    }
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve()
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`))
      }
    }
    xhr.onerror = () => reject(new Error('Upload failed: network error'))
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', 'application/zip')
    xhr.setRequestHeader('x-amz-tagging', 'object_lifecycle=lifecycle_archive')
    xhr.send(file)
  })

  // Finalize (verify zip exists, mark ready)
  const finalizeResponse = await apiClient.post<FinalizeUploadResponse>(
    `/admin/restore/snapshots/${encodeURIComponent(snapshotId)}/finalize`
  )

  return finalizeResponse.data.snapshot
}

/**
 * Queue restore job to run on jobs lambda.
 */
export async function runRestore(snapshotId: string, encryptionKey: string): Promise<RunRestoreResponse> {
  const response = await apiClient.post<RunRestoreResponse>(
    `/admin/restore/snapshots/${encodeURIComponent(snapshotId)}/run`,
    { encryptionKey }
  )
  return response.data
}

/**
 * Delete a snapshot.
 */
export async function deleteSnapshot(snapshotId: string): Promise<void> {
  await apiClient.delete(`/admin/restore/snapshots/${encodeURIComponent(snapshotId)}`)
}

/**
 * Fetch execution log from S3.
 */
export async function getSnapshotLog(snapshotId: string): Promise<string> {
  const response = await apiClient.get<string>(
    `/admin/restore/snapshots/${encodeURIComponent(snapshotId)}/log`,
    { responseType: 'text' }
  )
  return response.data
}

/**
 * Queue export job to create snapshot from current environment.
 */
export async function exportData(options: ExportOptions): Promise<ExportResponse> {
  const response = await apiClient.post<ExportResponse>('/admin/restore/export', options)
  return response.data
}

/**
 * Get presigned S3 download URL for snapshot.
 */
export async function getDownloadUrl(snapshotId: string): Promise<string> {
  const response = await apiClient.get<DownloadUrlResponse>(
    `/admin/restore/snapshots/${encodeURIComponent(snapshotId)}/download`
  )
  return response.data.url
}

/**
 * List available base images from shared S3 bucket. Sandbox only.
 */
export async function listBaseImages(): Promise<BaseImage[]> {
  const response = await apiClient.get<BaseImage[]>('/admin/restore/base-images')
  return response.data
}

/**
 * Import a base image into local snapshots. Sandbox only.
 */
export async function importBaseImage(key: string): Promise<RestoreSnapshot> {
  const response = await apiClient.post<RestoreSnapshot>('/admin/restore/base-images/import', { key })
  return response.data
}
